<template>
  <!-- A dropdown panel anchored under (or, when there is no room, over) its parent cell, ALWAYS inside the editor.
       The parent cell is `position: relative`; the visible region is the nearest `[data-pfe-bounds]` ancestor (the
       editor's scroll area). It picks the side with more room, caps its list height to what fits, and shifts left when
       it would cross the right edge — so no popup is ever clipped by the scroll area or the panel
       (cypress/e2e/post-op-editor.cy.js measures this with cy.assertWithinContainer). -->
  <div
    ref="menu"
    class="pm"
    :class="{ 'pm--up': up, 'pm--right': alignRight }"
    :style="{ minWidth: minWidth + 'px' }"
    data-pfe-menu
  >
    <div v-if="$scopedSlots.header" class="pm-head"><slot name="header" /></div>
    <div class="pm-list" :style="{ maxHeight: listMax + 'px' }">
      <slot />
    </div>
    <slot name="footer" />
  </div>
</template>

<script>
/**
 * Popover shell shared by CodePicker and FormPicker. Pure layout: it owns no data and no open/close state.
 * Re-measures when it mounts, when the window resizes, and when the editor's scroll area scrolls.
 */
const MARGIN = 12;       // breathing room between the menu and the edge of the visible area
const MIN_LIST = 120;    // never squeeze the list below this
const CHROME = 56;       // search row (and footer link) that share the menu's height with the list

export default {
  name: 'PfePopMenu',
  props: {
    /** The tallest the whole menu may be, in px. */
    maxHeight: { type: Number, default: 340 },
    minWidth: { type: Number, default: 0 },
  },
  data() {
    return { up: false, alignRight: false, listMax: this.maxHeight - CHROME };
  },
  mounted() {
    this.bounds = this.$el.closest('[data-pfe-bounds]');
    this.reposition = () => this.measure();
    window.addEventListener('resize', this.reposition);
    if (this.bounds) this.bounds.addEventListener('scroll', this.reposition, { passive: true });
    this.$nextTick(() => {
      this.measure();
      // keep the freshly opened menu in view (the row itself may be half off-screen)
      if (typeof this.$el.scrollIntoView === 'function') this.$el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.reposition);
    if (this.bounds) this.bounds.removeEventListener('scroll', this.reposition);
  },
  methods: {
    measure() {
      const el = this.$refs.menu;
      const anchor = el && el.parentElement;
      if (!el || !anchor) return;
      const area = this.bounds ? this.bounds.getBoundingClientRect() : { top: 0, bottom: window.innerHeight, right: window.innerWidth };
      const a = anchor.getBoundingClientRect();
      const below = area.bottom - a.bottom - MARGIN;
      const above = a.top - area.top - MARGIN;
      const wantBelow = below >= Math.min(this.maxHeight, 260) || below >= above;
      this.up = !wantBelow;
      const room = Math.max(MIN_LIST + CHROME, Math.min(this.maxHeight, wantBelow ? below : above));
      this.listMax = Math.max(MIN_LIST, room - CHROME);
      // horizontal: if the menu (anchored to the cell's left edge) would cross the visible right edge, anchor to its right edge
      this.alignRight = false;
      this.$nextTick(() => {
        const m = this.$refs.menu && this.$refs.menu.getBoundingClientRect();
        if (m && m.right > area.right - 8) this.alignRight = true;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.pm {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  width: max(100%, 320px);
  max-width: calc(100vw - 24px);
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(12, 15, 14, 0.16), 0 2px 6px rgba(12, 15, 14, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &--up { top: auto; bottom: calc(100% + 6px); }
  &--right { left: auto; right: 0; }
}
.pm-head {
  padding: 8px;
  border-bottom: 1px solid $pp-border;
}
.pm-list {
  overflow-y: auto;
  padding: 6px;
}
</style>
